import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Rifm } from 'rifm';
import styled, { css } from 'styled-components';
import { Button, CurrencyFormat, LabledInput } from '@lt/components';
import { formatCurrency } from '../../../utils/stringUtils';
import withMobile from '../hocs/withMobile';

// Кратное 1000
const MULTIPLE = 1000;
// Максимальный номинал сертификата
const MAX_VALUE = 1000000;

const getPlainPrice = (value) =>
  parseInt(String(value).replace(/[^\d]+/gi, ''), 10) || 0;
const formatInput = (value) => formatCurrency(getPlainPrice(value));

const PriceSelector = ({
  isMobile,
  isOrderCreated,
  presets,
  value,
  setValue,
  setStartTyping,
  setStopTyping,
}) => {
  const [isInputShown, setIsInputShown] = useState(false);

  const showInput = () => {
    setStartTyping();
    setValue(null);
    setIsInputShown(true);
  };
  const hideInput = () => setIsInputShown(false);

  const handleOnBlur = () => {
    const formattedValue = Math.min(
      Math.max(Math.round(value / MULTIPLE) * MULTIPLE, MULTIPLE),
      MAX_VALUE,
    );

    setStopTyping();
    setValue(formattedValue);
  };

  return (
    <StyledContainer>
      <StyledSelectGroup isMobile={isMobile}>
        {presets.map((pricePreset) => (
          <StyledButton
            isMobile={isMobile}
            disabled={isOrderCreated}
            key={pricePreset}
            active={!isInputShown && value === pricePreset}
            onClick={() => {
              hideInput();
              setValue(pricePreset);
            }}
          >
            <CurrencyFormat value={pricePreset} />
          </StyledButton>
        ))}
        <StyledButton
          isMobile={isMobile}
          disabled={isOrderCreated}
          active={isInputShown}
          onClick={showInput}
        >
          Другая
        </StyledButton>
      </StyledSelectGroup>
      {isInputShown && (
        <Rifm
          accept={/[\d.₽]/g}
          value={value}
          onChange={(e) => setValue(getPlainPrice(e))}
          format={formatInput}
        >
          {({ value: formattedValue, onChange }) => (
            <StyledLabledInput
              disabled={isOrderCreated}
              autoFocus
              isMobile={isMobile}
              maxLength={10}
              type="tel"
              label={`Введите сумму кратную ${formatCurrency(
                MULTIPLE,
              )}, но не более ${formatCurrency(MAX_VALUE)}`}
              onChange={onChange}
              onBlur={handleOnBlur}
              onFocus={setStartTyping}
              value={formattedValue}
            />
          )}
        </Rifm>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledLabledInput = styled(LabledInput)`
  margin-top: 6px;

  input {
    font-size: ${(props) => (props.isMobile ? '18px' : '14px')};
    font-weight: 500;
  }
`;

const StyledSelectGroup = styled.div`
  --button-border-radius: 2px;

  ${(props) =>
    props.isMobile &&
    css`
      --button-border-radius: 6px;
    `};

  display: flex;
  white-space: nowrap;
  border: 1px solid;
  border-color: ${(props) => props.theme.colors.frenchGray};
  border-radius: var(--button-border-radius);
`;

const StyledButton = styled(Button)`
  --button-border-radius: 2px;
  --button-width: 105px;
  --button-height: 35px;
  --separator-height: 31px;

  ${(props) =>
    props.isMobile &&
    css`
      --button-border-radius: 6px;
      --button-width: 75px;
      --button-height: 38px;
      --separator-height: 10px;
      padding: 0;
    `};

  position: relative;
  width: var(--button-width);
  height: var(--button-height);
  flex-grow: 1;
  font-size: 14px;
  color: ${(props) => props.theme.colors.blackLight};
  border: none;
  border-radius: 0;

  &:hover {
    color: ${(props) =>
      props.active ? props.theme.colors.white : props.theme.colors.blackBase};
  }

  ${(props) =>
    props.active &&
    css`
      z-index: 1;
      overflow: visible;
      color: ${props.theme.colors.white};

      &::before {
        position: absolute;
        z-index: -1;
        top: -1px;
        left: -1px;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        content: '';
        background-color: ${props.theme.colors.blue};
      }

      &:first-of-type::before {
        border-top-left-radius: var(--button-border-radius);
        border-bottom-left-radius: var(--button-border-radius);
      }

      &:last-of-type::before {
        border-top-right-radius: var(--button-border-radius);
        border-bottom-right-radius: var(--button-border-radius);
      }
    `};

  ${(props) =>
    !props.active &&
    css`
      &:not(:last-of-type)::after {
        position: absolute;
        top: calc(50% - var(--separator-height) / 2);
        right: 0;
        width: 1px;
        height: var(--separator-height);
        content: '';
        background-color: ${props.theme.colors.frenchGray};
      }
    `}
`;

PriceSelector.propTypes = {
  presets: PropTypes.arrayOf(PropTypes.number).isRequired,
  value: PropTypes.number,
  setValue: PropTypes.func.isRequired,
  setStartTyping: PropTypes.func.isRequired,
  setStopTyping: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isOrderCreated: PropTypes.bool.isRequired,
};
PriceSelector.defaultProps = {
  value: null,
  isMobile: false,
};

export default React.memo(withMobile(PriceSelector));
